// ./games/crash/ui/components/styles/AutoPlay.styles.ts

export const autoPlayStyles = (): AutoPlayModalStyles =>
  getAutoPlayModalStyles();

export type AutoPlayModalStyleResolver = (
  gameSlug?: string | null
) => AutoPlayModalStyles;

export interface AutoPlayModalStyles {
  overlay: React.CSSProperties;
  modal: React.CSSProperties;
  modalTablet: React.CSSProperties;
  modalMobile: React.CSSProperties;
  header: React.CSSProperties;
  title: React.CSSProperties;
  /** Header title font-size override on mobile portrait (skyx_new). */
  titleMobile?: React.CSSProperties;
  /** Header subtitle line under the title (skyx_new). */
  subtitle?: React.CSSProperties;
  /** Header subtitle font-size override on mobile portrait (skyx_new). */
  subtitleMobile?: React.CSSProperties;
  /** Wraps title+subtitle in a column so the close button stays flex-aligned (skyx_new). */
  headerTextGroup?: React.CSSProperties;
  closeButton: React.CSSProperties;
  /** Optional text close (×) when image assets are not tinted (skyx_new). */
  closeIcon?: React.CSSProperties;
  /** Close button hover state (skyx_new). */
  closeButtonHover?: React.CSSProperties;
  /** Close button size override on mobile portrait (skyx_new). */
  closeButtonMobile?: React.CSSProperties;
  section: React.CSSProperties;
  /** Extra padding/spacing override applied only to the "Number of Rounds" card (skyx_new). */
  sectionRounds?: React.CSSProperties;
  label: React.CSSProperties;
  centeredLabel: React.CSSProperties;
  /** "Number of Rounds" label size/spacing on mobile portrait (skyx_new). */
  centeredLabelMobile?: React.CSSProperties;
  roundsContainer: React.CSSProperties;
  /** Round-pill row gap on mobile portrait (skyx_new). */
  roundsContainerMobile?: React.CSSProperties;
  roundButton: React.CSSProperties;
  /** Round-pill padding/font-size override on mobile portrait, so all 5 fit on one row (skyx_new). */
  roundButtonMobile?: React.CSSProperties;
  roundButtonActive: React.CSSProperties;
  /** Active round-pill padding/font-size override on mobile portrait (skyx_new). */
  roundButtonActiveMobile?: React.CSSProperties;
  /** Inactive round-pill hover state (skyx_new). */
  roundButtonHover?: React.CSSProperties;
  toggleRow: React.CSSProperties;
  switch: React.CSSProperties;
  slider: React.CSSProperties;
  sliderChecked: React.CSSProperties;
  sliderBefore: React.CSSProperties;
  sliderBeforeChecked: React.CSSProperties;
  inputGroup: React.CSSProperties;
  inputButton: React.CSSProperties;
  iconButton: React.CSSProperties;
  inputField: React.CSSProperties;
  currencyLabel: React.CSSProperties;
  toggleLabel: React.CSSProperties;
  toggleLabelOff?: React.CSSProperties;
  toggleLabelOn?: React.CSSProperties;
  stopToggleHeader?: React.CSSProperties;
  stopToggleTextBlock?: React.CSSProperties;
  stopSectionRow?: React.CSSProperties;
  stopSectionCopy?: React.CSSProperties;
  stopSectionTitle?: React.CSSProperties;
  stopSectionDescription?: React.CSSProperties;
  stopSectionInput?: React.CSSProperties;
  /** Toggle + title + amount on one row (skyx_new). */
  stopSectionInlineRow?: React.CSSProperties;
  stopSectionInlineRowMobile?: React.CSSProperties;
  stopSectionTitleMobile?: React.CSSProperties;
  sectionMobile?: React.CSSProperties;
  stopAmountRowMobile?: React.CSSProperties;
  stopAmountInputShellMobile?: React.CSSProperties;
  inputButtonMobile?: React.CSSProperties;
  currencyLabelMobile?: React.CSSProperties;
  /** Minus / pill input / plus row (skyx_new — buttons outside input). */
  stopAmountRow?: React.CSSProperties;
  stopAmountInputShell?: React.CSSProperties;
  stopSectionMobileStack?: React.CSSProperties;
  stopSectionDescriptionBelow?: React.CSSProperties;
  footer: React.CSSProperties;
  /** Footer padding/border-top override on mobile portrait (skyx_new). */
  footerMobile?: React.CSSProperties;
  resetButton: React.CSSProperties;
  /** Reset button hover state (skyx_new). */
  resetButtonHover?: React.CSSProperties;
  /** Reset button radius/padding/font-size override on mobile portrait (skyx_new). */
  resetButtonMobile?: React.CSSProperties;
  startButton: React.CSSProperties;
  /** Start button radius/padding/font-size override on mobile portrait (skyx_new). */
  startButtonMobile?: React.CSSProperties;
  contentArea: React.CSSProperties;
  /** Scoped @keyframes CSS injected once via a <style> tag (skyx_new glow-pulse). */
  modalKeyframes?: string;

  // --- ADDED FOR IN-MODAL ERROR ---
  errorContainer: React.CSSProperties;
  errorMessage: React.CSSProperties;
  errorCloseButton: React.CSSProperties;
  /** Sky-X insufficient balance pill (no close button). */
  insufficientBalanceErrorContainer?: React.CSSProperties;
  /** Disabled stop-limit input border (skyx_new autoplay modal). */
  stopInputDisabledBorder?: string;
  /** Enabled stop-limit input border, overriding the shared bet-panel chrome (skyx_new autoplay modal). */
  stopAmountInputBorder?: string;
  /** Stop-limit input shell background, overriding the shared bet-panel chrome (skyx_new autoplay modal). */
  stopAmountInputBackground?: string;
  /** Currency label color, overriding the shared bet-panel chrome's multiplier color (skyx_new autoplay modal). */
  currencyLabelColor?: string;
  /** Stop-limit input shell resting box-shadow, overriding the shared bet-panel chrome's focus glow leak (skyx_new autoplay modal). */
  stopAmountInputShadow?: string;
  /** Stop-limit input shell border while being edited (skyx_new autoplay modal). */
  stopAmountInputFocusBorder?: string;
  /** Stop-limit input shell box-shadow while being edited (skyx_new autoplay modal). */
  stopAmountInputFocusShadow?: string;
  /** Error banner close button hover state, replacing the shared white text-shadow glow (skyx_new autoplay modal). */
  errorCloseButtonHover?: React.CSSProperties;
  // --- END ADDED ---
}

/** Core default styles, or game-provided resolver from platform UI config. */
export function resolveAutoPlayModalStyles(
  gameSlug: string | null | undefined,
  resolver?: AutoPlayModalStyleResolver
): AutoPlayModalStyles {
  if (resolver) {
    return resolver(gameSlug);
  }
  return getAutoPlayModalStyles();
}

export const getAutoPlayModalStyles = (): AutoPlayModalStyles => ({
  // Match Provably Fair / game-rules overlays: inset + overflowY auto so the
  // sheet scrolls inside iframes. Avoid height:100% — in portal iframes that
  // can resolve to content height and kill the scrollport.
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding:
      'max(16px, calc(var(--headerHeightDynamic, 42px) + 8px)) 16px max(16px, env(safe-area-inset-bottom, 0px))',
    overflowY: 'auto',
    overflowX: 'hidden',
    zIndex: 10000,
    pointerEvents: 'auto',
    touchAction: 'pan-y',
    boxSizing: 'border-box',
    WebkitOverflowScrolling: 'touch',
  },

  // ---------- DESKTOP MODAL ----------
  // Same scroll model as history round / Provably Fair: maxHeight panel +
  // internal contentArea overflow (header/footer stay put).
  modal: {
    background: '#1b1c1d',
    borderRadius: '12px',
    padding: '10px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: 'min(90vh, 860px)',
    color: '#FFFFFF',
    fontFamily: "'Inter', sans-serif",
    pointerEvents: 'auto',
    userSelect: 'none',

    margin: '0 auto',
    position: 'relative',
    top: '0',
    left: '0',
    transform: 'none',
    boxSizing: 'border-box',

    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    touchAction: 'pan-y',
  },

  // ---------- TABLET ----------
  modalTablet: {
    background: '#1b1c1d',
    borderRadius: '12px',
    padding: '10px',
    width: '100%',
    maxWidth: '480px',
    maxHeight: 'min(90vh, 860px)',
    color: '#FFFFFF',
    fontFamily: "'Inter', sans-serif",

    margin: '0 auto',
    position: 'relative',
    left: '0',
    top: '0',
    transform: 'none',
    boxSizing: 'border-box',

    pointerEvents: 'auto',
    userSelect: 'none',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    touchAction: 'pan-y',
  },

  // ---------- MOBILE ----------
  modalMobile: {
    background: '#1b1c1d',
    borderRadius: '12px',
    padding: '24px',
    width: '100%',
    maxWidth: '480px',
    maxHeight:
      'calc(100dvh - max(24px, env(safe-area-inset-top, 0px) + env(safe-area-inset-bottom, 0px)))',
    color: '#FFFFFF',
    fontFamily: "'Inter', sans-serif",

    margin: '0 auto',
    position: 'relative',
    left: '0',
    top: '0',
    transform: 'none',
    boxSizing: 'border-box',

    pointerEvents: 'auto',
    userSelect: 'none',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    touchAction: 'pan-y',
  },

  header: {
    display: 'flex',
    justifyContent: 'left',
    alignItems: 'center',
    marginBottom: '10px',
    position: 'relative',
    backgroundColor: '#2c2d30',
    borderRadius: '12px',
    userSelect: 'none',
    flexShrink: 0,
  },
  title: {
    marginLeft: '8px',
    marginRight: '40px',
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: 1.25,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#a0a3af',
    fontSize: '28px',
    cursor: 'pointer',
    position: 'absolute',
    right: '5px',
    top: '13px',
    boxShadow: 'none',
  },

  contentArea: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    touchAction: 'pan-y',
  },

  section: {
    marginBottom: '5px',
    padding: '15px 15px',
    backgroundColor: '#2c2d30',
    borderRadius: '12px',
  },

  label: {
    display: 'block',
    marginBottom: '12px',
    fontSize: '16px',
    color: '#a0a3af',
  },
  centeredLabel: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    color: '#FFFFFF',
    textAlign: 'center',
    fontWeight: '400',
    userSelect: 'none',
    letterSpacing: '0.5px',
  },
  roundsContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    userSelect: 'none',
    boxShadow: 'none',
  },

  roundButton: {
    flex: 1,
    maxWidth: '60px',
    padding: '8px',
    background: '#252528',
    border: '0.5px solid #848a9443',
    borderRadius: '20px',
    color: '#83878e',
    cursor: 'pointer',
    fontSize: '14px',
    userSelect: 'none',
  },
  roundButtonActive: {
    flex: 1,
    maxWidth: '60px',
    padding: '8px',
    border: '1px solid #60ae05',
    background: '#545b62',
    borderRadius: '20px',
    color: '#ffff',
    cursor: 'pointer',
    fontSize: '14px',
    userSelect: 'none',
    transition: 'all 0.3s ease',
  },

  toggleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    userSelect: 'none',
  },

  switch: {
    position: 'relative',
    display: 'inline-block',
    width: '36px',
    height: '24px',
    flexShrink: 0,
    userSelect: 'none',
  },
  slider: {
    position: 'absolute',
    cursor: 'pointer',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#0b0b0bae',
    borderRadius: '12px',
    color: '#a3a3a3',
  },
  sliderChecked: {
    backgroundColor: '#0b0b0bae',
  },
  sliderBefore: {
    position: 'absolute',
    content: '""',
    height: '16px',
    minWidth: '16px',
    maxWidth: '16px',
    left: '4px',
    bottom: '4px',
    backgroundColor: 'rgb(44, 45, 48)',
    borderRadius: '50%',
  },
  sliderBeforeChecked: {
    transform: 'translateX(12px)',
    backgroundColor: '#a3a3a3',
  },

  inputGroup: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#141516',
    borderRadius: '20px',
    padding: '4px',
    marginRight: '0px',
    userSelect: 'none',
  },
  inputButton: {
    background: '#2c2d30 ',
    border: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    color: '#FFFFFF',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    cursor: 'pointer',
    fontSize: '18px',
    fontWeight: 600,
    userSelect: 'none',
    flexShrink: 0,
    boxShadow: 'none',
  },
  iconButton: {
    width: '12px',
    height: '12px',
    objectFit: 'contain',
    userSelect: 'none',
    pointerEvents: 'none',
  },
  inputField: {
    width: '70px',
    background: 'transparent',
    border: 0,
    color: 'rgb(204, 204, 204)',
    cursor: 'text',
    padding: 0,
    textAlign: 'center',
    fontSize: '16px',
    fontWeight: 'bold',
    outline: 'none',
    MozAppearance: 'textfield',
    userSelect: 'none',
    WebkitTextFillColor: '#ccc',
    boxShadow: 'none',
  },
  currencyLabel: {
    padding: '0 8px',
    color: '#fff',
    fontSize: '14px',
    userSelect: 'none',
  },

  toggleLabel: {
    flex: '1 1 0',
    minWidth: 0,
    fontSize: '13px',
    lineHeight: 1.25,
    marginLeft: '0',
    color: '#d2d2d2',
    userSelect: 'none',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },

  stopSectionRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
  },
  stopSectionCopy: {
    flex: '1 1 0',
    minWidth: 0,
  },
  stopSectionTitle: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: 1.25,
    color: '#ffffff',
    marginBottom: '4px',
  },
  stopSectionDescription: {
    margin: 0,
    fontSize: '11px',
    lineHeight: 1.35,
    color: '#a0a3af',
  },
  stopSectionInput: {
    flexShrink: 0,
    marginTop: '2px',
  },

  footer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '1px',
    userSelect: 'none',
    background: 'rgb(44, 45, 48)',
    borderRadius: '12px',
    padding: '16px',
    marginTop: '10px',
    flexShrink: 0,
  },

  resetButton: {
    padding: '2px',
    background: '#d07206',
    border: '1px solid #ffbd71',
    borderRadius: '12px',
    color: '#FFFFFF',
    cursor: 'pointer',
    minWidth: '80px',
    height: '24px',
    marginRight: '7.5px',
    marginTop: '4px',
    userSelect: 'none',
    fontSize: '15px',
    fontWeight: '450',
    lineHeight: '8px',
  },

  startButton: {
    padding: '0px',
    background: '#28a909',
    border: 'solid 1px #b2f2a3',
    borderRadius: '12px',
    color: '#FFFFFF',
    marginLeft: '7.5px',
    fontSize: '21px',
    fontWeight: '450',
    cursor: 'pointer',
    width: '160px',
    height: '30px',
    userSelect: 'none',
    lineHeight: '12px',
  },

  // ------------ ERROR STYLES ------------
  errorContainer: {
    background: '#b3021b',
    borderRadius: '10px',
    padding: '10px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
    color: '#FFFFFF',
    fontFamily: "'Inter', sans-serif",
    userSelect: 'none',
    flexShrink: 0,
  },

  errorMessage: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '18px',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    flex: 1,
    minWidth: 0,
    userSelect: 'none',
  },

  errorCloseButton: {
    background: 'transparent',
    border: 'none',
    color: ' #97a4ae',
    opacity: 0.8,
    fontSize: '1.5rem',
    fontWeight: 700,
    cursor: 'pointer',
    padding: '0 4px',
    marginLeft: '10px',
    textShadow: '0 1px 0 #97a4ae',
    userSelect: 'none',
  },
});
